import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BootstrapColor, DarkColor } from '@/types/css-variables.types';
import { ButtonSize } from '@shared/components/atoms/buttons/e-btn2/e-btn2.component';

interface SelectOption {
  value: string;
  label: string;
}

interface ButtonColorDemo {
  color: BootstrapColor | DarkColor;
  label: string;
}

interface ColorToken {
  variable: string;
  label: string;
  hex?: string;
  rgb?: string;
}

@Component({
  selector: 'app-style-guide-page',
  templateUrl: './style-guide-page.component.html',
  styleUrls: ['./style-guide-page.component.scss'],
  standalone: false
})
export class StyleGuidePageComponent implements OnInit {
  textValue = '';
  textValueFilled = 'Mechanics in Motion';
  textareaValue = '';
  nativeSelectValue = '';
  disabledValue = 'Niet bewerkbaar';

  checkboxChecked = true;
  checkboxUnchecked = false;
  radioValue: 'a' | 'b' | 'c' = 'a';

  singleSelect = new FormControl<string | null>(null);
  multiSelect = new FormControl<string[]>([]);
  disabledSelect = new FormControl<string | null>({ value: 'human-design', disabled: true });

  menuSelectedLabel = 'Human by Design';

  primaryLoading = false;

  readonly selectOptions: SelectOption[] = [
    { value: 'mechanics', label: 'Mechanics in Motion' },
    { value: 'human-design', label: 'Human by Design' },
    { value: 'platform', label: 'Platform Beheer' },
    { value: 'awareness', label: 'Empowerment through awareness' }
  ];

  readonly buttonColors: ButtonColorDemo[] = [
    { color: 'primary', label: 'primary (amber CTA)' },
    { color: 'cta', label: 'cta (coral)' },
    { color: 'secondary', label: 'secondary' },
    { color: 'success', label: 'success' },
    { color: 'danger', label: 'danger' },
    { color: 'warning', label: 'warning' },
    { color: 'info', label: 'info' }
  ];

  readonly buttonSizes: ButtonSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

  readonly colorTokens: ColorToken[] = [
    // Merk & CTA
    { variable: '--primary', label: 'primary (dusk-slate, header)' },
    { variable: '--secondary', label: 'secondary' },
    { variable: '--amber', label: 'amber' },
    { variable: '--amber-light', label: 'amber-light (buttons)' },
    { variable: '--cta', label: 'cta' },
    { variable: '--shell-subbar', label: 'shell-subbar' },
    // Oppervlakken
    { variable: '--content-canvas', label: 'content-canvas' },
    { variable: '--content-surface', label: 'content-surface' },
    { variable: '--content-fill', label: 'content-fill' },
    { variable: '--highlight', label: 'highlight' },
    { variable: '--between-highlight-and-separator', label: 'between-highlight-and-separator' },
    { variable: '--separator', label: 'separator' },
    { variable: '--scrollbar-thumb', label: 'scrollbar-thumb' },
    { variable: '--scrollbar-thumb-hover', label: 'scrollbar-thumb-hover' },
    // Status-accent (alerts & banners)
    { variable: '--info', label: 'info (= dusk-slate, alert-accent)' },
    { variable: '--info-25', label: 'info-25 (alert-bg)' },
    { variable: '--warning', label: 'warning' },
    { variable: '--warning-25', label: 'warning-25 (alert-bg)' },
    { variable: '--danger', label: 'danger' },
    { variable: '--danger-25', label: 'danger-25 (alert-bg)' },
    { variable: '--success', label: 'success' },
    { variable: '--success-25', label: 'success-25 (alert-bg)' },
    // Dark systeembanner achtergronden
    { variable: '--announcement-info-bg', label: 'announcement-info-bg' },
    { variable: '--announcement-warning-bg', label: 'announcement-warning-bg' },
    { variable: '--announcement-danger-bg', label: 'announcement-danger-bg' },
    { variable: '--announcement-success-bg', label: 'announcement-success-bg' }
  ];

  onMenuSelect(label: string): void {
    this.menuSelectedLabel = label;
  }

  triggerPrimaryLoading(): void {
    this.primaryLoading = true;
    setTimeout(() => (this.primaryLoading = false), 1500);
  }

  ngOnInit(): void {
    const root = getComputedStyle(document.documentElement);
    this.colorTokens.forEach((token) => {
      const raw = root.getPropertyValue(token.variable).trim();
      const parsed = this.parseColor(raw);
      if (parsed) {
        token.hex = this.toHex(parsed);
        token.rgb = `rgb(${parsed.r}, ${parsed.g}, ${parsed.b})`;
      }
    });
  }

  private parseColor(raw: string): { r: number; g: number; b: number } | null {
    if (!raw) {
      return null;
    }

    const hexMatch = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => char + char)
          .join('');
      }
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      };
    }

    const rgbMatch = raw.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (rgbMatch) {
      return {
        r: Math.round(Number(rgbMatch[1])),
        g: Math.round(Number(rgbMatch[2])),
        b: Math.round(Number(rgbMatch[3]))
      };
    }

    return null;
  }

  private toHex({ r, g, b }: { r: number; g: number; b: number }): string {
    const channel = (value: number): string => value.toString(16).padStart(2, '0');
    return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
  }
}
